import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Image, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaGoogle, FaTwitter, FaFacebook } from 'react-icons/fa';
import { requestNotificationPermission, initializeUserNotifications } from '../config/firebase';
import authService from '../services/authService';
import './LoginPage.css';

// Import your logo or use a placeholder
const chitchatLogo = '/assets/chatbot.jpg';

const Login = ({ setIsAuthenticated }) => {
    const [credentials, setCredentials] = useState({
        email: '',
        password: ''
    });
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Check if user is already logged in on component mount
    useEffect(() => {
        if (authService.isLoggedIn()) {
            setIsAuthenticated(true);
            navigate('/chat');
        }
    }, [navigate, setIsAuthenticated]);

    const handleChange = (e) => {
        setCredentials({
            ...credentials,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Validate input
        if (!credentials.email || !credentials.password) {
            setError('Email and password are required');
            setIsLoading(false);
            return;
        }

        try {
            // Get FCM token for push notifications
            const fcmToken = await requestNotificationPermission();
            
            // Use authService for login
            const response = await authService.login({
                ...credentials,
                fcm_token: fcmToken,
                remember_me: rememberMe
            });

            // Extract user data
            const { data } = response;
            
            // Initialize user-specific notifications if FCM token exists
            if (fcmToken) {
                localStorage.setItem('fcm_token', fcmToken);
                
                // Initialize user-specific notifications
                await initializeUserNotifications(data.id);
            }

            // If using rememberMe, set longer session duration flag
            if (rememberMe) {
                localStorage.setItem('rememberMe', 'true');
            }

            // Set up socket connection with authentication
            try {
                const socket = window.socket; // Assuming socket is initialized globally
                if (socket && socket.connected) {
                    socket.emit('authenticate', authService.getToken());
                    console.log('Socket authenticated');
                }
            } catch (socketError) {
                console.error('Socket authentication error:', socketError);
                // Continue login process even if socket auth fails
            }

            // Update authentication state
            setIsAuthenticated(true);

            // Navigate to the chat page upon successful login
            navigate('/chat');
        } catch (error) {
            console.error('Login error:', error);

            // Extract error message from response if available
            const errorMessage = error.response?.data?.message || 'Failed to login. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container fluid className="login-container p-0">
            <Row className="m-0 h-100">
                {/* Left Side - Login Form */}
                <Col md={5} className="login-form-container p-4 p-md-5">
                    <div className="logo-container mb-4">
                        <Image src={chitchatLogo} alt="Chitchat Logo" className="logo" />
                        <span className="logo-text">
                            <span className="logo-text-bold">AI Agents</span>
                        </span>
                    </div>

                    <div className="welcome-text mb-4">
                        <h4 className="mb-2">Hello Everyone, we are Customer Service AI Agents</h4>
                        <p className="text-muted">Welcome to our platform, please login to your account.</p>
                    </div>

                    {error && <Alert variant="danger">{error}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="formEmail">
                            <Form.Label style={{ float: 'left', fontWeight: 'bold' }}>Email Address</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={credentials.email}
                                onChange={handleChange}
                                placeholder="Enter your email"
                                className="py-2"
                                autoComplete="email"
                                required />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formPassword">
                            <Form.Label style={{ float: 'left', fontWeight: 'bold' }}>Password</Form.Label>
                            <Form.Control 
                                type="password"
                                name="password"
                                value={credentials.password} 
                                onChange={handleChange}
                                placeholder="Enter your password"
                                className="py-2"
                                autoComplete="current-password"
                                required
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Form.Check
                                type="checkbox"
                                id="rememberMe"
                                label="Remember Me"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <a href="#forgot" className="forgot-link">Forgot Password!</a>
                        </div>

                        <div className="d-flex flex-column flex-md-row gap-3 mb-4">
                            <Button
                                variant="primary"
                                type="submit"
                                className="login-btn py-2 flex-grow-1"
                                disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Logging in...
                                    </>
                                ) : 'Login'}
                            </Button>
                            <Link to="/register" className="flex-grow-1">
                                <Button
                                    variant="secondary"
                                    className="signup-btn py-2 w-100">
                                    SignUp
                                </Button>
                            </Link>
                        </div>
                    </Form>

                    <div className="social-login-container text-center">
                        <p className="divider">
                            <span>OR Connect with</span>
                        </p>
                        <div className="social-icons d-flex justify-content-center gap-3 mt-3">
                            <Button variant="light" className="social-icon google-icon">
                                <FaGoogle />
                            </Button>
                            <Button variant="light" className="social-icon twitter-icon">
                                <FaTwitter />
                            </Button>
                            <Button variant="light" className="social-icon facebook-icon">
                                <FaFacebook />
                            </Button>
                        </div>
                    </div>

                    <div className="terms-container text-center mt-4">
                        <span className="terms-text">
                            <span className="text-danger">•</span> Terms and condition &nbsp;&amp;&nbsp; Privacy policy
                        </span>
                    </div>
                </Col>

                {/* Right Side - Illustration */}
                <Col md={7} className="illustration-container d-none d-md-block p-0">
                    <div className="illustration-bg">
                        <div className="illustration-content">
                            <img
                                src="/assets/background.jpg"
                                alt="Chat Illustration"
                                className="chat-illustration"
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default Login;